/**
 * TeamPipelinesSection: the batch pipelines a team may submit to
 * (TRA-863), granted by staff. One switch per registered pipeline. A
 * grant is the whole entitlement (a valid API key alone never runs a
 * pipeline), so flipping a switch calls the API at once, and a granted
 * row says who granted it and when. The pipelines a customer sees on
 * their Models page are exactly the rows switched on here.
 */

import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { getApiErrorDetail } from '@/api/client'
import { getListPipelinesApiPipelinesGetQueryKey } from '@/api/generated/batches/batches'
import {
  getListTeamPipelinesApiInternalTeamsTeamIdPipelinesGetQueryKey,
  useGrantTeamPipelineApiInternalTeamsTeamIdPipelinesPipelinePut,
  useListRegisteredPipelinesApiInternalPipelinesGet,
  useListTeamPipelinesApiInternalTeamsTeamIdPipelinesGet,
  useRevokeTeamPipelineApiInternalTeamsTeamIdPipelinesPipelineDelete,
} from '@/api/generated/internal/internal'
import type { PipelineGrantItem, PipelineInfo } from '@/api/generated/model'
import { SettingsList, SettingsListRow } from '@/components/settings/SettingsList'
import { SettingsPageHeader } from '@/components/settings/SettingsPageHeader'
import { SettingsSection } from '@/components/settings/SettingsSection'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'

import type { TeamConfigSectionProps } from './sections'

export function TeamPipelinesSection({ section, team }: TeamConfigSectionProps): React.JSX.Element {
  const queryClient = useQueryClient()
  const registry = useListRegisteredPipelinesApiInternalPipelinesGet()
  const grants = useListTeamPipelinesApiInternalTeamsTeamIdPipelinesGet(team.id)
  const grantsKey = getListTeamPipelinesApiInternalTeamsTeamIdPipelinesGetQueryKey(team.id)
  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: grantsKey })
    // The customer-facing read behind the Models page's Pipelines band.
    // The query client holds cached responses for five minutes
    // (main.tsx), so without this a staff member who grants a pipeline
    // and walks to Models sees the cached empty answer until a reload.
    // Its key carries no team: the team rides in a header, and switching
    // teams reloads the app, so invalidating always targets the right one.
    void queryClient.invalidateQueries({ queryKey: getListPipelinesApiPipelinesGetQueryKey() })
  }
  const grant = useGrantTeamPipelineApiInternalTeamsTeamIdPipelinesPipelinePut({
    mutation: {
      onSuccess: refresh,
      onError: (error: unknown) => {
        toast.error(getApiErrorDetail(error) ?? 'The grant was not saved')
      },
    },
  })
  const revoke = useRevokeTeamPipelineApiInternalTeamsTeamIdPipelinesPipelineDelete({
    mutation: {
      onSuccess: refresh,
      onError: (error: unknown) => {
        toast.error(getApiErrorDetail(error) ?? 'The grant was not revoked')
      },
    },
  })

  const pipelines = registry.data?.status === 200 ? registry.data.data.pipelines : []
  const granted = new Map<string, PipelineGrantItem>(
    (grants.data?.status === 200 ? grants.data.data.grants : []).map((row) => [row.pipeline, row]),
  )
  const loading = registry.isLoading || grants.isLoading
  const failed = registry.isError || grants.isError
  const pending = grant.isPending || revoke.isPending

  return (
    <div>
      <SettingsPageHeader section={section} />
      <SettingsSection
        title="Grants"
        description="Switch a pipeline on to let this team submit batches to it. The team's Models page lists the pipelines switched on here."
      >
        {loading ? (
          <Skeleton className="h-14 w-full" />
        ) : failed ? (
          <p className="text-sm text-destructive" role="alert">
            Failed to load pipelines.
          </p>
        ) : pipelines.length === 0 ? (
          <p className="text-sm text-muted-foreground">No pipelines are registered.</p>
        ) : (
          <SettingsList>
            {pipelines.map((pipeline) => (
              <PipelineRow
                key={pipeline.name}
                pipeline={pipeline}
                grant={granted.get(pipeline.name)}
                disabled={pending}
                onCheckedChange={(checked) => {
                  const variables = { teamId: team.id, pipeline: pipeline.name }
                  if (checked) grant.mutate(variables)
                  else revoke.mutate(variables)
                }}
              />
            ))}
          </SettingsList>
        )}
      </SettingsSection>
    </div>
  )
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

function PipelineRow({
  pipeline,
  grant,
  disabled,
  onCheckedChange,
}: {
  pipeline: PipelineInfo
  grant: PipelineGrantItem | undefined
  disabled: boolean
  onCheckedChange: (checked: boolean) => void
}): React.JSX.Element {
  const id = `pipeline-grant-${pipeline.name}`
  return (
    <SettingsListRow className="items-start py-3">
      <div className="min-w-0 flex-1">
        <Label htmlFor={id}>{pipeline.display_name}</Label>
        <p className="font-mono text-xs text-muted-foreground">{pipeline.name}</p>
        <p className="mt-1 text-xs text-muted-foreground">{pipeline.description}</p>
        {grant !== undefined && (
          <p className="mt-1 text-xs text-muted-foreground">
            Granted by {grant.granted_by} on {formatDate(grant.created_at)}
          </p>
        )}
      </div>
      <Switch id={id} checked={grant !== undefined} disabled={disabled} onCheckedChange={onCheckedChange} />
    </SettingsListRow>
  )
}
